<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { LogoFormica, LogoPoweredByBlp, LogoPoweredByBlpDark } from '@blpde/nx-ui';
import {
  LOGIN_LINK_NAMES,
  loginBrandingColor,
  loginBrandingImageUrl,
  loginBrandingText,
  type LoginBranding,
} from '@/loginBranding';

/**
 * The page around the sign-in form (edge rebuild plan W19): the logo, the
 * title and subtitle, the footer with the imprint, privacy and help links, the
 * background — each from the branding of the hostname, else the platform's own
 * (the Formica logo, „Bei Formica anmelden", the page surface). The form is the
 * default slot. The login view and the settings hub's preview paint with it.
 *
 * A branding image is an `<img>`: it is a customer's picture, not a kit
 * control. With a dark logo the page shows it in dark mode (`data-theme`).
 */
const props = withDefaults(
  defineProps<{
    branding?: LoginBranding | null;
    /** The API origin a branding image link is relative to. */
    origin?: string;
    /** Inside a preview: no full-height page. */
    embedded?: boolean;
    /** Replaces the title (a step of the sign-in with a title of its own). */
    title?: string;
    /** Show the branding's subtitle under the title. */
    showSubtitle?: boolean;
  }>(),
  { branding: null, origin: '', embedded: false, title: undefined, showSubtitle: true },
);

const { t, locale } = useI18n();

const login = computed(() => props.branding?.login);

function image(source: string | undefined): string | undefined {
  return source ? loginBrandingImageUrl(source, props.origin) : undefined;
}

const logo = computed(() => image(login.value?.logo));
const logoDark = computed(() => image(login.value?.logo_dark));
const heading = computed(
  () => props.title ?? loginBrandingText(login.value?.title, locale.value) ?? t('auth.login_title'),
);
const subtitle = computed(() =>
  props.showSubtitle && !props.title ? loginBrandingText(login.value?.subtitle, locale.value) : undefined,
);
const footer = computed(() => loginBrandingText(login.value?.footer, locale.value));
const links = computed(() =>
  LOGIN_LINK_NAMES.flatMap((name) => {
    const href = login.value?.links?.[name];
    return href ? [{ name, href }] : [];
  }),
);
// The page of a customer names Formica discreetly; the platform's own page is
// the Formica logo already.
const poweredBy = computed(() => Boolean(logo.value) && login.value?.show_powered_by !== false);

const backgroundStyle = computed(() => {
  const background = login.value?.background;
  if (background?.image) {
    return { backgroundImage: `url("${image(background.image)}")` };
  }
  if (background?.color) return { background: loginBrandingColor(background.color) };
  return undefined;
});
const hasImage = computed(() => Boolean(login.value?.background?.image));
</script>

<template>
  <div
    class="login-wrapper"
    :class="{ 'is-embedded': embedded, 'has-image': hasImage }"
    :style="backgroundStyle"
    data-testid="login-frame"
  >
    <div class="login-column">
      <template v-if="logo">
        <img class="login-logo login-logo-light" :class="{ 'has-dark': logoDark }" :src="logo" :alt="t('auth.logo_alt')" />
        <img v-if="logoDark" class="login-logo login-logo-dark" :src="logoDark" :alt="t('auth.logo_alt')" />
      </template>
      <component :is="LogoFormica" v-else class="login-logo" />

      <h2 class="login-title">{{ heading }}</h2>
      <p v-if="subtitle" class="login-subtitle">{{ subtitle }}</p>

      <slot />

      <footer v-if="footer || links.length || poweredBy" class="login-footer">
        <p v-if="footer" class="login-footer-text">{{ footer }}</p>
        <nav v-if="links.length" class="login-links" :aria-label="t('auth.links.label')">
          <a v-for="link in links" :key="link.name" :href="link.href" target="_blank" rel="noopener noreferrer">
            {{ t(`auth.links.${link.name}`) }}
          </a>
        </nav>
        <template v-if="poweredBy">
          <component :is="LogoPoweredByBlp" class="login-powered-by login-powered-by-light" />
          <component :is="LogoPoweredByBlpDark" class="login-powered-by login-powered-by-dark" />
        </template>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.login-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  padding: var(--nxt-space-xl);
  /* The page surface, not a hard white: this is the only screen the shell paints
     before any app mounts, so it has to follow the same axes as everything after
     it — brand theme and dark mode included. A branding background replaces it. */
  background: var(--nxt-bg-app);
  background-position: center;
  background-size: cover;
}

.login-wrapper.is-embedded {
  min-height: 0;
  padding: var(--nxt-space-lg);
}

.login-column {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-md);
  width: 100%;
  max-width: var(--nxt-panel-width-xl);
}

/* Text on a picture stays readable: the column sits on the surface. */
.has-image .login-column {
  padding: var(--nxt-space-xl);
  background: var(--nxt-surface-bg);
  border-radius: var(--nxt-radius-lg);
}

.login-logo {
  display: block;
  width: auto;
  max-width: 100%;
  height: var(--nxt-tile-size-md);
  /* A leaked scoped rule (svg{padding:1em}) from the shell otherwise squashes
     the logo's aspect ratio — pin padding to 0 so it renders at full width. */
  padding: 0;
  align-self: center;
  margin-bottom: var(--nxt-space-xs);
  object-fit: contain;
}

.login-logo-dark,
.login-powered-by-dark {
  display: none;
}

:global([data-theme='dark']) .login-logo-light.has-dark {
  display: none;
}

:global([data-theme='dark']) .login-logo-dark {
  display: block;
}

:global([data-theme='dark']) .login-powered-by-light {
  display: none;
}

:global([data-theme='dark']) .login-powered-by-dark {
  display: block;
}

.login-title {
  text-align: center;
  font-size: var(--nxt-font-size-xl);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg);
  margin: 0 0 var(--nxt-space-xs);
}

.login-subtitle {
  margin: calc(-1 * var(--nxt-space-xs)) 0 0;
  text-align: center;
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg-subtle);
  line-height: var(--nxt-line-height-normal);
}

.login-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--nxt-space-sm);
  margin-top: var(--nxt-space-md);
  text-align: center;
}

.login-footer-text {
  margin: 0;
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
  line-height: var(--nxt-line-height-normal);
  white-space: pre-line;
}

.login-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--nxt-space-md);
  font-size: var(--nxt-font-size-xs);
}

.login-links a {
  color: var(--nxt-fg-subtle);
}

.login-powered-by {
  display: block;
  width: auto;
  height: var(--nxt-space-md);
  color: var(--nxt-fg-muted);
}
</style>
